import React, { ComponentProps, useCallback, useRef, useState } from 'react';
import { Modal, TouchableOpacity } from 'react-native';

import { Box, Text } from '@components/Restyle';
import Icon from '@components/shared/Icon/Icon';
import { PopupTaskFilesFilter } from '@components/Web/PopupTaskFilesFilter.web';
import { useOutsideClick } from '@hooks/useOutsideClick.web';

import { getScaledSize, SCALED_DIMENSIONS } from '../../../themes/theme';

type PositionTuple = ['left' | 'right', 'top' | 'bottom'];

type Position = PositionTuple | 'top' | 'bottom' | 'left' | 'right';

type Props = Omit<ComponentProps<typeof TouchableOpacity>, 'onPress'> & {
  onDismiss?: () => void;
  onSelectMedia?: (selected: boolean) => void;
  onSelectDocument?: (selected: boolean) => void;
  onSearchChange?: (searchText: string) => void;
  onClearAll?: () => void;
  mediaSelected?: boolean;
  documentSelected?: boolean;
  searchValue?: string;
  filterCount?: number;
  setIsShowFilter: React.Dispatch<React.SetStateAction<boolean>>;
  boxProps: Partial<{
    position: Position;
    offset: [number, number] | number;
  }>;
  maxHeight?: number;
};

export const PopupTaskFilesFilterButtonWeb = ({
  onDismiss,
  onSelectMedia,
  onSelectDocument,
  onSearchChange,
  onClearAll,
  mediaSelected = false,
  documentSelected = false,
  searchValue = '',
  filterCount = 0,
  setIsShowFilter,
  boxProps: { position, offset, ...popupPropsRest },
}: Props) => {
  const [visible, setVisible] = useState(false);
  const [mouseOver, setMouseOver] = useState(false);

  const togglePopup = () => {
    setVisible((v) => !v);
    setIsShowFilter((v) => !v);
  };

  const ref2 = useRef<HTMLDivElement>(null);

  const isEventPointInModal = (event: MouseEvent) => {
    const rect = ref2?.current?.getBoundingClientRect();
    return (
      rect &&
      event.x > rect.x &&
      event.x < rect.x + rect.width &&
      event.y > rect.y &&
      event.y < rect.y + rect.height
    );
  };
  const handleOutsideClick = useCallback((event: MouseEvent) => {
    if (!isEventPointInModal(event)) {
      setVisible(false);
      setIsShowFilter(false);
    }
  }, []);

  const ref = useOutsideClick<HTMLDivElement>(handleOutsideClick);

  // Store the natural dimensions of the popup container
  const [layout, setLayout] = useState<
    { width: number; height: number } | undefined
  >();

  const adjustBounds = (
    position: Props['boxProps']['position'] = 'bottom',
    offset: Props['boxProps']['offset'] = 0,
    client: DOMRect,
    popupLayout: { width: number; height: number }
  ) => {
    const [offsetLeftRight, offsetTopBottom] =
      typeof offset === 'number' ? [offset, offset] : offset;

    const normalizedPosition = (typeof position !== 'string' && position) ||
      ((position === 'left' || position === 'right') && [position, 'bottom']) ||
      ((position === 'top' || position === 'bottom') && ['left', position]) || [
        'left',
        'bottom',
      ];

    const [leftRight, topBottom] = normalizedPosition;
    const bounds = {
      top:
        topBottom === 'top'
          ? client.top - popupLayout.height - offsetTopBottom
          : client.top + client.height + offsetTopBottom - 8,
      left:
        leftRight === 'left'
          ? client.left + offsetLeftRight - 16
          : client.left + client.width - popupLayout.width - offsetLeftRight,
    };

    return bounds;
  };

  const adjustedBounds =
    ref.current &&
    layout &&
    adjustBounds(position, offset, ref.current.getBoundingClientRect(), layout);

  const onModalDismiss = () => {
    onDismiss?.();
    setVisible(false);
    setIsShowFilter(false);
  };

  const backgroundColor1 = () => {
    if (mouseOver || filterCount > 0 || visible) return 'grey01';
    return 'white';
  };

  return (
    <Box ref={ref}>
      <TouchableOpacity
        onPress={togglePopup}
        activeOpacity={0.7}
        accessibilityLabel='Filter'>
        <Box
          height={getScaledSize(SCALED_DIMENSIONS.BUTTON_HEIGHT)}
          flexDirection='row'
          px='m'
          backgroundColor={backgroundColor1()}
          borderRadius='xs'
          alignItems='center'
          justifyContent='center'
          style={{ paddingVertical: 10 }}
          // @ts-ignore - onMouseEnter/onMouseLeave are valid web props
          onMouseEnter={() => {
            setMouseOver(true);
          }}
          // @ts-ignore - onMouseEnter/onMouseLeave are valid web props
          onMouseLeave={() => {
            setMouseOver(false);
          }}>
          <Icon
            variant='s'
            name='AlignCenterDesc'
            color='black'
            width={16}
            height={16}
          />
          <Text variant='webSmall' color='black' marginLeft='xs'>
            Filter
          </Text>
          {filterCount > 0 && (
            <>
              <Box
                width={20}
                height={20}
                backgroundColor='greenSecondary'
                borderRadius='s'
                alignItems='center'
                justifyContent='center'
                marginLeft='xs'>
                <Text variant='webBodyHintSubline' color='white'>
                  {filterCount}
                </Text>
              </Box>
              <TouchableOpacity
                onPress={(e) => {
                  e?.stopPropagation?.();
                  onClearAll?.();
                }}
                hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
                activeOpacity={0.7}
                style={{ marginLeft: 8 }}
                accessibilityLabel='Clear filters'>
                <Icon name='X' variant='s' color='black' />
              </TouchableOpacity>
            </>
          )}
        </Box>
      </TouchableOpacity>

      {!layout && (
        <Box
          position='absolute'
          visible={!layout}
          onLayout={({ nativeEvent: { layout } }) => {
            setLayout((curr) => curr ?? layout);
          }}>
          <PopupTaskFilesFilter
            onSelectMedia={onSelectMedia}
            onSelectDocument={onSelectDocument}
            onSearchChange={onSearchChange}
            onClearAll={onClearAll}
            mediaSelected={mediaSelected}
            documentSelected={documentSelected}
            searchValue={searchValue}
            {...popupPropsRest}
          />
        </Box>
      )}
      <Modal
        visible={visible}
        transparent
        onDismiss={() => onModalDismiss()}
        onRequestClose={() => onModalDismiss()}>
        <Box ref={ref2} style={{ ...adjustedBounds }} width={layout?.width}>
          <Box flexShrink={1}>
            <PopupTaskFilesFilter
              onSelectMedia={onSelectMedia}
              onSelectDocument={onSelectDocument}
              onSearchChange={onSearchChange}
              onClearAll={onClearAll}
              mediaSelected={mediaSelected}
              documentSelected={documentSelected}
              searchValue={searchValue}
              {...popupPropsRest}
            />
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};
